import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Sparkles, Crown, Zap, Check, Loader2, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// Stripe LIVE mode product/price mappings
const COIN_PACKAGES = [
  {
    id: "100-coins",
    name: "100 CallyCoins",
    coins: 100,
    price: 5,
    priceId: "price_1ShH5kDhLy9uoTQ7ouDkNb7l",
    icon: Coins,
    popular: false,
  },
  {
    id: "500-coins",
    name: "500 CallyCoins",
    coins: 500,
    price: 20,
    priceId: "price_1ShH63DhLy9uoTQ7CVJSvRyP",
    icon: Zap,
    popular: true,
    bonus: "Save 20%",
  },
  {
    id: "1000-coins",
    name: "1000 CallyCoins",
    coins: 1000,
    price: 35,
    priceId: "price_1ShH6IDhLy9uoTQ7jSbP58b7",
    icon: Crown,
    popular: false,
    bonus: "Best Value!",
  },
];

const SUBSCRIPTIONS = [
  {
    id: "basic",
    name: "Basic Monthly",
    coins: 200,
    price: 9.99,
    priceId: "price_1ShH6UDhLy9uoTQ7I3vPV54t",
    productId: "prod_TeaHSgEehcJyIK",
    features: ["200 CallyCoins/month", "Standard support"],
    icon: Sparkles,
  },
  {
    id: "premium",
    name: "Premium Monthly",
    coins: 500,
    price: 19.99,
    priceId: "price_1ShH6qDhLy9uoTQ7RZhIOcXG",
    productId: "prod_TeaHPF0IdzW2oH",
    features: ["500 CallyCoins/month", "Priority support", "Early access to events"],
    icon: Crown,
    popular: true,
  },
];

export default function Store() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, session } = useAuth();
  const { t, formatCurrency, locale } = useLocale();
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<{
    subscribed: boolean;
    product_id: string | null;
    subscription_end: string | null;
  } | null>(null);

  useEffect(() => {
    const success = searchParams.get("success");
    const canceled = searchParams.get("canceled");
    
    if (success === "true") {
      toast({
        title: locale.language === "pt" ? "Pagamento Confirmado! 🎉" : "Payment Successful! 🎉",
        description: locale.language === "pt" 
          ? "Seus CallyCoins foram adicionados à sua conta." 
          : "Your CallyCoins have been added to your account.",
      });
      // Clear URL params to prevent toast on refresh
      window.history.replaceState({}, "", "/store");
    } else if (canceled === "true") {
      toast({
        title: locale.language === "pt" ? "Pagamento Cancelado" : "Payment Canceled",
        description: locale.language === "pt" 
          ? "Seu pagamento não foi concluído." 
          : "Your payment was not completed.",
        variant: "destructive",
      });
      window.history.replaceState({}, "", "/store");
    }
  }, [searchParams, toast, locale.language]);

  useEffect(() => {
    if (session) {
      checkSubscription();
    }
  }, [session]);

  const checkSubscription = async () => {
    if (!session?.access_token) return;
    
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      // Handle auth errors gracefully - user may need to re-login
      if (error?.message?.includes("401") || data?.error?.includes("Authentication")) {
        console.log("Session expired, user may need to re-login");
        setSubscription({ subscribed: false, product_id: null, subscription_end: null });
        return;
      }
      
      if (!error && data && !data.error) {
        setSubscription(data);
      }
    } catch (err) {
      console.error("Error checking subscription:", err);
      setSubscription({ subscribed: false, product_id: null, subscription_end: null });
    }
  };

  const handlePurchase = async (priceId: string, mode: "payment" | "subscription") => {
    if (!user || !session?.access_token) {
      navigate("/auth", { state: { from: "/store" } });
      return;
    }

    setLoadingItem(priceId);
    try {
      // Get fresh session to ensure token is valid
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession?.access_token) {
        toast({
          title: locale.language === "pt" ? "Sessão Expirada" : "Session Expired",
          description: locale.language === "pt" 
            ? "Por favor, faça login novamente" 
            : "Please log in again",
          variant: "destructive",
        });
        navigate("/auth", { state: { from: "/store" } });
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, mode },
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to create checkout session",
        variant: "destructive",
      });
    } finally {
      setLoadingItem(null);
    }
  };

  const handleManageSubscription = async () => {
    setLoadingItem("manage");
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to open customer portal",
        variant: "destructive",
      });
    } finally {
      setLoadingItem(null);
    }
  };

  const isCurrentSubscription = (productId: string) => {
    return subscription?.subscribed && subscription?.product_id === productId;
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t("store.title")} <span className="text-gradient">{t("store.titleHighlight")}</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t("store.description")}
            </p>
          </motion.div>

          {/* Subscription Management */}
          {subscription?.subscribed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12"
            >
              <div className="glass-card p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("store.activeSubscription")}</p>
                  <p className="text-lg font-semibold text-foreground">
                    {subscription.product_id === "prod_TeaHSgEehcJyIK" ? t("store.basicMonthly") : 
                     subscription.product_id === "prod_TeaHPF0IdzW2oH" ? t("store.premiumMonthly") : "Premium Plan"}
                  </p>
                  {subscription.subscription_end && (
                    <p className="text-sm text-muted-foreground">
                      {t("store.renews")} {new Date(subscription.subscription_end).toLocaleDateString(locale.language === "pt" ? "pt-BR" : "en-US")}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={loadingItem === "manage"}
                >
                  {loadingItem === "manage" ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Settings className="h-4 w-4 mr-2" />
                  )}
                  {t("store.manageSubscription")}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Coin Packages */}
          <section className="mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl font-bold mb-6 flex items-center gap-2"
            >
              <Coins className="h-6 w-6 text-primary" />
              {t("store.coinPackages")}
            </motion.h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {COIN_PACKAGES.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className={`glass-card-hover p-6 relative ${
                    pkg.popular ? "border-primary/50" : ""
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        {t("store.mostPopular")}
                      </span>
                    </div>
                  )}
                  {pkg.bonus && (
                    <div className="absolute -top-3 right-4">
                      <span className="bg-success text-success-foreground text-xs font-semibold px-3 py-1 rounded-full">
                        {pkg.id === "500-coins" ? t("store.save") : t("store.bestValue")}
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center pt-2">
                    <pkg.icon className="h-12 w-12 mx-auto mb-4 text-primary" />
                    <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                    <p className="text-3xl font-bold text-gradient mb-4">${pkg.price}</p>
                    <p className="text-muted-foreground mb-6">
                      {pkg.coins} coins • {t("store.oneTimePurchase")}
                    </p>
                    <Button
                      className="w-full btn-glow"
                      onClick={() => handlePurchase(pkg.priceId, "payment")}
                      disabled={loadingItem === pkg.priceId}
                    >
                      {loadingItem === pkg.priceId ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      {t("store.buyNow")}
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Subscriptions */}
          <section>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl font-bold mb-6 flex items-center gap-2"
            >
              <Sparkles className="h-6 w-6 text-primary" />
              {t("store.monthlySubscriptions")}
            </motion.h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {SUBSCRIPTIONS.map((sub, index) => {
                const isCurrent = isCurrentSubscription(sub.productId);
                const subName = sub.id === "basic" ? t("store.basicMonthly") : t("store.premiumMonthly");
                const features = sub.id === "basic" 
                  ? [`200 ${t("store.coinsPerMonth")}`, t("store.standardSupport")]
                  : [`500 ${t("store.coinsPerMonth")}`, t("store.prioritySupport"), t("store.earlyAccess")];
                
                return (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className={`glass-card-hover p-6 relative ${
                      sub.popular ? "border-primary/50" : ""
                    } ${isCurrent ? "border-success/50 ring-2 ring-success/20" : ""}`}
                  >
                    {sub.popular && !isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                          {t("store.recommended")}
                        </span>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-success text-success-foreground text-xs font-semibold px-3 py-1 rounded-full">
                          {t("store.yourPlan")}
                        </span>
                      </div>
                    )}
                    
                    <div className="text-center pt-2">
                      <sub.icon className="h-12 w-12 mx-auto mb-4 text-primary" />
                      <h3 className="text-xl font-bold mb-2">{subName}</h3>
                      <p className="text-3xl font-bold text-gradient mb-1">
                        ${sub.price}
                        <span className="text-base font-normal text-muted-foreground">{t("store.perMonth")}</span>
                      </p>
                      <p className="text-muted-foreground mb-4">
                        {sub.coins} {t("store.coinsEveryMonth")}
                      </p>
                      
                      <ul className="text-left space-y-2 mb-6">
                        {features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Check className="h-4 w-4 text-success flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        className={`w-full ${isCurrent ? "" : "btn-glow"}`}
                        variant={isCurrent ? "outline" : "default"}
                        onClick={() => isCurrent ? handleManageSubscription() : handlePurchase(sub.priceId, "subscription")}
                        disabled={loadingItem === sub.priceId}
                      >
                        {loadingItem === sub.priceId ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {isCurrent ? t("store.managePlan") : t("store.subscribe")}
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
